/**
 * CoachPanel — G4D CP2 (Frontend).
 *
 * Inline chat surface on Wizard Step 10. Mounts BELOW the quality-
 * score actionability bridge; on `/proposals/{id}/draft` it also
 * mounts BELOW the section list (premium only).
 *
 * All state derivations live in `coach_helpers.js`; every static
 * user-facing string comes from `coach_copy.js`. This file is
 * deliberately thin — rendering shell + effect wiring.
 *
 * IMPORTANT — DO NOT introduce any "we'll approve" / "guaranteed"
 * language in this file. `coach_copy.js` is the single source of
 * truth for user-visible text and is banned-phrase-swept at test
 * time.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Loader2,
  Lock,
  MessageSquare,
  RefreshCcw,
  SendHorizontal,
  Sparkles,
  X,
} from "lucide-react";

import { api, formatApiError } from "@/api/client";
import PremiumUpgradePanel from "@/components/grants/PremiumUpgradePanel";
import InsertSuggestionModal from "./InsertSuggestionModal";
import InsertUndoToast from "./InsertUndoToast";
import {
  COACH_ACTIONS_HEADING,
  COACH_ACTION_ASK_FOLLOWUP_LABEL,
  COACH_ACTION_DISMISS_LABEL,
  COACH_ACTION_INSERT_DISABLED_TOOLTIP,
  COACH_ACTION_INSERT_LABEL,
  COACH_ACTION_PREVIEW_LABEL,
  COACH_COUNTER_LABEL,
  COACH_COUNTER_TOTAL,
  COACH_DISCLAIMER,
  COACH_EMPTY_BODY,
  COACH_EMPTY_TITLE,
  COACH_ERROR_HINT,
  COACH_INPUT_MAX_CHARS,
  COACH_INPUT_PLACEHOLDER,
  COACH_KILL_SWITCH_NOTICE,
  COACH_LOAD_ERROR,
  COACH_LOCKED_BODY,
  COACH_LOCKED_CTA,
  COACH_LOCKED_TITLE,
  COACH_PANEL_HEADING,
  COACH_PANEL_KICKER,
  COACH_PANEL_SUBHEADING,
  COACH_RATE_LIMIT_PREFIX,
  COACH_RATE_LIMIT_SUFFIX,
  COACH_RETRY_LABEL,
  COACH_SEND_LABEL,
} from "./coach_copy";
import {
  askFollowupPayload,
  canInsertOnActionForTier,
  charLimitState,
  derivePanelState,
  isFallbackReply,
  isFreeTierConversation,
  normalizeCoachMessage,
} from "./coach_helpers";
import { sectionDisplayTitle } from "./quality_helpers";


/**
 * Adapter: turn a `/coach-context` payload into the same
 * `{ sections: { key: { status } } }` shape that
 * `coach_helpers.hasGeneratedSection` expects. Only the
 * `has_generated_content` bit matters — no per-section content is
 * needed for the locked-state derivation.
 */
function _proposalFromCoachContext(coachContext) {
  if (!coachContext) return null;
  const generated = new Set(coachContext.generated_section_keys || []);
  const sections = {};
  for (const key of coachContext.section_keys || []) {
    sections[key] = { status: generated.has(key) ? "passed" : "missing" };
  }
  return {
    id: coachContext.proposal_id,
    title: coachContext.title,
    sections,
  };
}

/**
 * @param {object} props
 * @param {string} props.proposalId — the proposal being coached
 * @param {object} props.proposal   — current proposal doc for the
 *                                    locked-state gate (sections dict)
 * @param {string=} props.wizardBackTo — route the "Back to Generate"
 *                                    CTA links to (default step 9)
 * @param {string=} props.testIdPrefix — override the panel's
 *                                    data-testid root. Default: "coach-panel"
 */
export default function CoachPanel({
  proposalId,
  proposal: proposalProp,
  coachContext,
  wizardBackTo,
  testIdPrefix = "coach-panel",
  onProposalMutated,
}) {
  const [conversation, setConversation] = useState(null);
  const [proposal, setProposal] = useState(
    proposalProp || _proposalFromCoachContext(coachContext) || null,
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [rateLimit, setRateLimit] = useState(null); // { retryAt }
  const [rateLimitRemaining, setRateLimitRemaining] = useState(0);
  const [errorHint, setErrorHint] = useState(null);
  const [failedContent, setFailedContent] = useState(null);
  const [dismissedActionIds, setDismissedActionIds] = useState(new Set());
  const listRef = useRef(null);

  // G4D CP3 — Insert Suggestion modal + Undo toast state.
  // `insertModal`: { messageId, suggestion } | null — controls modal visibility.
  // `undoToast`: { auditId, sectionTitle, undoExpiresAt } | null — one at a time.
  const [insertModal, setInsertModal] = useState(null);
  const [undoToast, setUndoToast] = useState(null);

  // A caller who passed `coachContext` is on the free-tier shell —
  // they've already been discriminated by the wizard, so
  // downstream we can skip a redundant `/api/proposals/{id}` fetch
  // (which would 402 anyway).
  const isFreeShell = !!coachContext;

  // Refetch conversation + (if needed) proposal for the locked-state
  // gate. Runs on mount and after every send (which triggers reload).
  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (isFreeShell) {
        // Free-tier shell — proposal shape is derived from the
        // coach-context payload once at mount; the conversation
        // still needs its own GET (server enforces the 3-msg cap).
        const convRes = await api.get(`/proposals/${proposalId}/coach/conversation`);
        setConversation(convRes.data);
      } else {
        const [convRes, proposalRes] = await Promise.all([
          api.get(`/proposals/${proposalId}/coach/conversation`),
          proposalProp
            ? Promise.resolve({ data: proposalProp })
            : api.get(`/proposals/${proposalId}`),
        ]);
        setConversation(convRes.data);
        setProposal(proposalRes.data);
      }
    } catch (err) {
      // A 402 on the proposal fetch (free-tier ProposalDraft path)
      // is unreachable here — the CoachPanel is never mounted on
      // the free ProposalDraft (per the CP2 gate). If it does
      // happen, degrade gracefully.
      setLoadError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [proposalId, proposalProp, isFreeShell]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Countdown for premium rate-limit banner.
  useEffect(() => {
    if (!rateLimit) return undefined;
    const tick = () => {
      const secondsLeft = Math.max(
        0,
        Math.ceil((rateLimit.retryAt - Date.now()) / 1000),
      );
      setRateLimitRemaining(secondsLeft);
      if (secondsLeft <= 0) setRateLimit(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [rateLimit]);

  // Auto-scroll to the newest message on message-list changes.
  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [conversation?.messages?.length]);

  const panelState = useMemo(
    () =>
      derivePanelState({
        proposal,
        conversation,
        isLoading: loading,
        loadError,
      }),
    [proposal, conversation, loading, loadError],
  );
  const isFree = isFreeTierConversation(conversation);
  const remaining = isFree ? conversation?.remaining_free_messages ?? 0 : null;
  const limitState = charLimitState(draft.length);

  const visibleMessages = useMemo(() => {
    const raw = conversation?.messages || [];
    return raw.map((m) => {
      const norm = normalizeCoachMessage(m);
      if (norm.role !== "coach" || !norm.recommended_actions) return norm;
      // Filter out any dismissed action_ids from local render state.
      const filtered = norm.recommended_actions.filter(
        (a) => !dismissedActionIds.has(a.action_id),
      );
      return { ...norm, recommended_actions: filtered };
    });
  }, [conversation?.messages, dismissedActionIds]);

  const send = useCallback(
    async (content) => {
      const trimmed = (content || "").trim();
      if (!trimmed) return;
      if (trimmed.length > COACH_INPUT_MAX_CHARS) return;
      setSending(true);
      setErrorHint(null);
      setFailedContent(null);
      try {
        await api.post(`/proposals/${proposalId}/coach/messages`, {
          content: trimmed,
        });
        setDraft("");
        // Refetch full conversation so both user + coach messages
        // land in order + counters update. Slight extra network for
        // simplicity — no optimistic UI needed at CP2 scope.
        await reload();
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 429) {
          // Premium rate-limit — inline banner + countdown; keep
          // draft so user can retry cleanly.
          const hint =
            typeof detail?.retry_after_hint === "number"
              ? detail.retry_after_hint
              : 60;
          setRateLimit({ retryAt: Date.now() + hint * 1000 });
          setDraft(trimmed);
        } else if (status === 402 && detail?.code === "coach_free_cap_reached") {
          // Free cap reached — reload to swap into "capped" state.
          await reload();
        } else if (status === 422 && detail?.code === "no_proposal_content_for_coach") {
          // Someone deleted the last passed section between mount
          // and send. Reload to swap into locked state.
          await reload();
        } else {
          // Transient error — surface the retry affordance on the
          // failed user bubble.
          setFailedContent(trimmed);
          setErrorHint(formatApiError(err) || COACH_ERROR_HINT);
        }
      } finally {
        setSending(false);
      }
    },
    [proposalId, reload],
  );

  const onSubmit = useCallback(
    (e) => {
      e?.preventDefault?.();
      send(draft);
    },
    [draft, send],
  );

  const onAskFollowup = useCallback(
    (action) => {
      const payload = askFollowupPayload(action);
      if (!payload) return;
      send(payload);
    },
    [send],
  );

  const onDismissAction = useCallback((action) => {
    setDismissedActionIds((prev) => {
      const next = new Set(prev);
      next.add(action.action_id);
      return next;
    });
  }, []);

  const onRetryFailed = useCallback(() => {
    if (failedContent) send(failedContent);
  }, [failedContent, send]);

  // G4D CP3 — Insert Suggestion click handler. Opens the modal with
  // the (message_id, structured_suggestion) pair. The chip button
  // itself is gated on `canInsertOnActionForTier(action, isFree)`.
  const onInsertClicked = useCallback(({ messageId, action }) => {
    if (!action?.structured_suggestion) return;
    setInsertModal({
      messageId,
      suggestion: action.structured_suggestion,
    });
  }, []);

  const onInserted = useCallback(async ({ audit_id, section_title, undo_expires_at }) => {
    // Close modal + surface undo toast. Replacing any pre-existing
    // toast — only one at a time (per spec).
    setInsertModal(null);
    setUndoToast({
      auditId: audit_id,
      sectionTitle: section_title,
      undoExpiresAt: undo_expires_at,
    });
    // Always fetch fresh proposal after mutation — bypasses the
    // proposalProp cache used by `reload()` so both this panel AND
    // the parent (via `onProposalMutated`) reflect the new content.
    try {
      const propRes = await api.get(`/proposals/${proposalId}`);
      setProposal(propRes.data);
      onProposalMutated?.(propRes.data);
    } catch { /* keep going — toast is still valid */ }
  }, [proposalId, onProposalMutated]);

  const onUndone = useCallback(async () => {
    // Refresh so the reverted content is visible. Toast handles its
    // own dismissal after the confirmation flash.
    try {
      const propRes = await api.get(`/proposals/${proposalId}`);
      setProposal(propRes.data);
      onProposalMutated?.(propRes.data);
    } catch { /* keep going */ }
  }, [proposalId, onProposalMutated]);

  const onDismissToast = useCallback(() => {
    setUndoToast(null);
  }, []);

  return (
    <section
      data-testid={testIdPrefix}
      className="mt-8 rounded-3xl border border-v2-border bg-v2-surface p-6 flex flex-col gap-4"
    >
      <PanelHeader />

      {panelState === "loading" && <SkeletonList />}

      {panelState === "load_error" && (
        <p
          data-testid={`${testIdPrefix}-load-error`}
          className="text-v2-body-sm text-v2-textMuted"
        >
          {loadError || COACH_LOAD_ERROR}
        </p>
      )}

      {panelState === "locked" && (
        <LockedState
          wizardBackTo={wizardBackTo || `/proposals/${proposalId}/edit?step=9`}
          isFreeShell={isFreeShell}
        />
      )}

      {panelState === "empty" && <EmptyState />}

      {(panelState === "active" || panelState === "capped") && (
        <MessageList
          messages={visibleMessages}
          listRef={listRef}
          onAskFollowup={onAskFollowup}
          onDismissAction={onDismissAction}
          onRetryFailed={onRetryFailed}
          onInsertClicked={onInsertClicked}
          isFree={isFree}
          errorHint={errorHint}
          failedContent={failedContent}
        />
      )}

      <DisclosureStrip />

      {panelState === "capped" && (
        <div data-testid={`${testIdPrefix}-cap-cta`}>
          <PremiumUpgradePanel scope="coach" />
        </div>
      )}

      {rateLimit && rateLimitRemaining > 0 && (
        <div
          data-testid={`${testIdPrefix}-rate-limited-banner`}
          className="rounded-2xl px-4 py-2 text-v2-body-sm"
          style={{
            background: "rgba(148,163,184,0.10)",
            border: "1px solid rgba(148,163,184,0.35)",
            color: "var(--v2-textMuted, #94a3b8)",
          }}
        >
          {COACH_RATE_LIMIT_PREFIX}
          {rateLimitRemaining}
          {COACH_RATE_LIMIT_SUFFIX}
        </div>
      )}

      {panelState !== "locked" &&
        panelState !== "loading" &&
        panelState !== "load_error" && (
          <ComposerRow
            isFreeCapped={panelState === "capped"}
            isFree={isFree}
            remaining={remaining}
            draft={draft}
            onDraftChange={setDraft}
            limitState={limitState}
            sending={sending}
            onSubmit={onSubmit}
            testIdPrefix={testIdPrefix}
          />
        )}

      {/* G4D CP3 — Insert Suggestion modal + Undo toast (Premium). */}
      {insertModal && (
        <InsertSuggestionModal
          proposalId={proposalId}
          messageId={insertModal.messageId}
          suggestion={insertModal.suggestion}
          onClose={() => setInsertModal(null)}
          onInserted={onInserted}
        />
      )}
      {undoToast && (
        <InsertUndoToast
          proposalId={proposalId}
          auditId={undoToast.auditId}
          sectionTitle={undoToast.sectionTitle}
          undoExpiresAt={undoToast.undoExpiresAt}
          onUndone={onUndone}
          onDismiss={onDismissToast}
        />
      )}
    </section>
  );
}


function PanelHeader() {
  return (
    <header className="flex items-start gap-3">
      <span
        className="inline-flex items-center justify-center rounded-full w-9 h-9 flex-shrink-0"
        style={{
          background: "rgba(167,139,250,0.14)",
          border: "1px solid rgba(167,139,250,0.35)",
        }}
      >
        <MessageSquare className="w-4 h-4 text-v2-accent" />
      </span>
      <div className="min-w-0">
        <p className="text-v2-caption uppercase tracking-[0.28em] text-v2-textMuted">
          {COACH_PANEL_KICKER}
        </p>
        <h3 className="text-v2-body font-semibold text-v2-text mt-1">
          {COACH_PANEL_HEADING}
        </h3>
        <p className="text-v2-caption text-v2-textMuted mt-1 leading-relaxed">
          {COACH_PANEL_SUBHEADING}
        </p>
      </div>
    </header>
  );
}


function LockedState({ wizardBackTo, isFreeShell }) {
  // G4D CP2 fix (2026-07-20) — free-tier shell can't generate
  // sections (that's a premium feature), so the locked-state CTA
  // MUST route them to the upgrade flow instead of Step 9. Body
  // copy + testId also change so the tester agent can distinguish
  // the two variants.
  const testId = isFreeShell ? "coach-locked-state-free" : "coach-locked-state";
  const title = COACH_LOCKED_TITLE;
  const body = isFreeShell
    ? "Section generation is a Premium feature. Upgrade to generate proposal sections and unlock the AI Grant Coach on this proposal."
    : COACH_LOCKED_BODY;
  const ctaLabel = isFreeShell ? "Upgrade to Premium" : COACH_LOCKED_CTA;
  const target = isFreeShell ? "/proposals" : wizardBackTo;
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-dashed border-v2-border bg-v2-surfaceSubtle/40 p-6 flex flex-col gap-3"
    >
      <div className="flex items-center gap-2">
        <Lock className="w-4 h-4 text-v2-accent" />
        <p className="text-v2-body-sm font-semibold text-v2-text">
          {title}
        </p>
      </div>
      <p className="text-v2-caption text-v2-textMuted leading-relaxed">
        {body}
      </p>
      <Link
        data-testid={isFreeShell ? "coach-locked-cta-free" : "coach-locked-cta"}
        to={target}
        className="self-start inline-flex items-center gap-2 rounded-full px-4 py-2 text-v2-caption font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors"
      >
        {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}


function EmptyState() {
  return (
    <div
      data-testid="coach-empty-state"
      className="rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 p-5"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-v2-accent mt-1" />
        <div>
          <p className="text-v2-body-sm font-semibold text-v2-text">
            {COACH_EMPTY_TITLE}
          </p>
          <p className="text-v2-caption text-v2-textMuted mt-1 leading-relaxed">
            {COACH_EMPTY_BODY}
          </p>
        </div>
      </div>
    </div>
  );
}


function SkeletonList() {
  return (
    <div
      data-testid="coach-loading-skeleton"
      className="flex flex-col gap-3"
      aria-hidden
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="rounded-2xl px-4 py-3"
          style={{
            background: "rgba(148,163,184,0.06)",
            border: "1px solid rgba(148,163,184,0.15)",
          }}
        >
          <div
            className="rounded-md"
            style={{
              width: i % 2 === 0 ? "60%" : "80%",
              height: "10px",
              background: "rgba(148,163,184,0.16)",
            }}
          />
          <div
            className="rounded-md mt-2"
            style={{
              width: i % 2 === 0 ? "40%" : "70%",
              height: "10px",
              background: "rgba(148,163,184,0.10)",
            }}
          />
        </div>
      ))}
    </div>
  );
}


function MessageList({
  messages,
  listRef,
  onAskFollowup,
  onDismissAction,
  onRetryFailed,
  onInsertClicked,
  isFree,
  errorHint,
  failedContent,
}) {
  return (
    <div
      ref={listRef}
      data-testid="coach-message-list"
      className="flex flex-col gap-3 max-h-[520px] overflow-y-auto pr-1"
    >
      {messages.map((m) => (
        <MessageBubble
          key={m.id}
          message={m}
          onAskFollowup={onAskFollowup}
          onDismissAction={onDismissAction}
          onInsertClicked={onInsertClicked}
          isFree={isFree}
        />
      ))}
      {failedContent && (
        <div
          data-testid="coach-failed-user-bubble"
          className="rounded-2xl border border-dashed p-3 flex flex-col gap-2"
          style={{
            borderColor: "rgba(252,165,165,0.5)",
            background: "rgba(252,165,165,0.06)",
          }}
        >
          <p className="text-v2-body-sm text-v2-text">{failedContent}</p>
          <p className="text-v2-caption" style={{ color: "#FCA5A5" }}>
            {errorHint}
          </p>
          <button
            type="button"
            data-testid="coach-retry-button"
            onClick={onRetryFailed}
            className="self-start inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-v2-caption font-semibold"
            style={{
              background: "rgba(167,139,250,0.14)",
              color: "var(--v2-accent, #5D50C8)",
              border: "1px solid rgba(167,139,250,0.35)",
            }}
          >
            <RefreshCcw className="w-3.5 h-3.5" /> {COACH_RETRY_LABEL}
          </button>
        </div>
      )}
    </div>
  );
}


function MessageBubble({ message, onAskFollowup, onDismissAction, onInsertClicked, isFree }) {
  const isUser = message.role === "user";
  const isFallback = isFallbackReply(message);
  const testId = isUser ? "coach-message-user" : "coach-message-coach";
  return (
    <div
      data-testid={testId}
      className="rounded-2xl px-4 py-3"
      style={
        isUser
          ? {
              background: "rgba(148,163,184,0.06)",
              border: "1px solid rgba(148,163,184,0.20)",
              alignSelf: "flex-end",
              maxWidth: "80%",
            }
          : {
              background: "rgba(167,139,250,0.05)",
              border: "1px solid rgba(167,139,250,0.20)",
              alignSelf: "flex-start",
              maxWidth: "92%",
            }
      }
    >
      {isFallback && (
        <p
          data-testid="coach-kill-switch-notice"
          className="text-v2-caption mb-2 leading-relaxed"
          style={{ color: "var(--v2-textMuted, #94a3b8)" }}
        >
          {COACH_KILL_SWITCH_NOTICE}
        </p>
      )}
      <p className="text-v2-body-sm text-v2-text whitespace-pre-wrap leading-relaxed">
        {message.content}
      </p>
      {message.recommended_actions && message.recommended_actions.length > 0 && (
        <ActionChipList
          actions={message.recommended_actions}
          messageId={message.id}
          onAskFollowup={onAskFollowup}
          onDismissAction={onDismissAction}
          onInsertClicked={onInsertClicked}
          isFree={isFree}
        />
      )}
    </div>
  );
}


function ActionChipList({ actions, messageId, onAskFollowup, onDismissAction, onInsertClicked, isFree }) {
  return (
    <div
      data-testid="coach-actions-list"
      className="mt-3 flex flex-col gap-2 border-t border-v2-border pt-3"
    >
      <p className="text-v2-caption uppercase tracking-[0.18em] text-v2-textMuted">
        {COACH_ACTIONS_HEADING}
      </p>
      <ul className="flex flex-col gap-2">
        {actions.map((action) => (
          <ActionChip
            key={action.action_id}
            action={action}
            messageId={messageId}
            onAskFollowup={onAskFollowup}
            onDismissAction={onDismissAction}
            onInsertClicked={onInsertClicked}
            isFree={isFree}
          />
        ))}
      </ul>
    </div>
  );
}


function ActionChip({ action, messageId, onAskFollowup, onDismissAction, onInsertClicked, isFree }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const type = action.action_type;
  const target = action.target_section_key;
  const insertEnabled = canInsertOnActionForTier(action, !!isFree);
  return (
    <li
      data-testid={`coach-action-${type}`}
      className="rounded-xl border border-v2-border bg-v2-surfaceSubtle/40 px-3 py-2 flex flex-col gap-2"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-v2-body-sm text-v2-text flex-1 min-w-0">
          {action.title}
          {target && (
            <span
              data-testid={`coach-action-target-${target}`}
              className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-v2-caption uppercase tracking-[0.14em] text-v2-textMuted"
              style={{
                background: "rgba(148,163,184,0.10)",
                border: "1px solid rgba(148,163,184,0.30)",
              }}
            >
              {sectionDisplayTitle(target)}
            </span>
          )}
        </p>
        {type === "insert_suggestion" && (
          <button
            type="button"
            data-testid="coach-action-insert-suggestion-btn"
            disabled={!insertEnabled}
            title={insertEnabled ? undefined : COACH_ACTION_INSERT_DISABLED_TOOLTIP}
            onClick={insertEnabled ? () => onInsertClicked?.({ messageId, action }) : undefined}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-v2-caption font-semibold transition-colors ${
              insertEnabled ? "hover:bg-v2-accent hover:text-v2-background" : "opacity-60 cursor-not-allowed"
            }`}
            style={{
              background: "rgba(167,139,250,0.12)",
              color: "var(--v2-accent, #5D50C8)",
              border: "1px solid rgba(167,139,250,0.35)",
            }}
          >
            {COACH_ACTION_INSERT_LABEL}
          </button>
        )}
        {type === "ask_followup" && (
          <button
            type="button"
            data-testid="coach-action-ask-followup-btn"
            onClick={() => onAskFollowup(action)}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-v2-caption font-semibold"
            style={{
              background: "rgba(167,139,250,0.14)",
              color: "var(--v2-accent, #5D50C8)",
              border: "1px solid rgba(167,139,250,0.35)",
            }}
          >
            {COACH_ACTION_ASK_FOLLOWUP_LABEL}
          </button>
        )}
        {type === "dismiss" && (
          <button
            type="button"
            data-testid="coach-action-dismiss-btn"
            onClick={() => onDismissAction(action)}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-v2-caption text-v2-textMuted hover:text-v2-text"
            style={{
              background: "transparent",
              border: "1px solid rgba(148,163,184,0.30)",
            }}
          >
            <X className="w-3.5 h-3.5" /> {COACH_ACTION_DISMISS_LABEL}
          </button>
        )}
      </div>
      {type === "insert_suggestion" && action.structured_suggestion && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            data-testid="coach-action-preview-toggle"
            onClick={() => setPreviewOpen((v) => !v)}
            className="self-start text-v2-caption text-v2-accent hover:underline"
          >
            {previewOpen ? "Hide preview ↑" : `${COACH_ACTION_PREVIEW_LABEL} ↓`}
          </button>
          {previewOpen && (
            <div
              data-testid="coach-action-preview"
              className="rounded-lg px-3 py-2 text-v2-body-sm text-v2-text leading-relaxed"
              style={{
                background: "rgba(148,163,184,0.05)",
                border: "1px dashed rgba(148,163,184,0.30)",
              }}
            >
              {action.structured_suggestion.suggestion_text}
              <p className="mt-2 text-v2-caption text-v2-textMuted">
                Rationale: {action.structured_suggestion.rationale}
              </p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}


function DisclosureStrip() {
  return (
    <div
      data-testid="coach-disclaimer"
      className="text-v2-caption text-v2-textMuted leading-relaxed border-l-2 pl-3"
      style={{ borderColor: "rgba(167,139,250,0.55)" }}
    >
      {COACH_DISCLAIMER}
    </div>
  );
}


function ComposerRow({
  isFreeCapped,
  isFree,
  remaining,
  draft,
  onDraftChange,
  limitState,
  sending,
  onSubmit,
  testIdPrefix,
}) {
  const overLimit = limitState === "over";
  const disabled = isFreeCapped || sending || overLimit || draft.trim().length === 0;
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      {isFree && !isFreeCapped && (
        <p
          data-testid="coach-remaining-counter"
          className="text-v2-caption text-v2-textMuted"
        >
          {COACH_COUNTER_LABEL}: {remaining} of {COACH_COUNTER_TOTAL}
        </p>
      )}
      <div className="flex gap-2 items-start">
        <textarea
          data-testid="coach-input"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder={COACH_INPUT_PLACEHOLDER}
          disabled={isFreeCapped || sending}
          rows={2}
          maxLength={COACH_INPUT_MAX_CHARS + 400}
          className="min-w-0 flex-1 rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 px-3 py-2 text-v2-body-sm text-v2-text placeholder:text-v2-textMuted focus:outline-none focus:border-v2-accent disabled:opacity-60 resize-none"
        />
        <button
          type="submit"
          data-testid="coach-send"
          disabled={disabled}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-v2-caption font-semibold text-v2-background bg-v2-accent hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <SendHorizontal className="w-3.5 h-3.5" />
          )}{" "}
          {COACH_SEND_LABEL}
        </button>
      </div>
      {(limitState === "warn" || limitState === "over") && (
        <p
          data-testid="coach-char-limit-warning"
          className="text-v2-caption self-end"
          style={{
            color:
              limitState === "over"
                ? "#FCA5A5"
                : "var(--v2-textMuted, #94a3b8)",
          }}
        >
          {draft.length} / {COACH_INPUT_MAX_CHARS}
        </p>
      )}
      {isFreeCapped && (
        // Additional in-context anchor for the tester agent (the
        // PremiumUpgradePanel already renders the CTA above via
        // `coach-cap-cta`, but this hint anchors the input row).
        <p className="text-v2-caption text-v2-textMuted" data-testid={`${testIdPrefix}-cap-input-hint`}>
          Free Coach messages exhausted for this proposal.
        </p>
      )}
    </form>
  );
}
